/**
 * The NPC catalogue — the single source of truth for what NPCs exist is
 * `npc.store` (repo root); this module imports its generated projection
 * `npcs.data.ts` (do not edit — run `node tools/npc-store.js` or save in
 * the knobs app's NPCs tab). The committed catalog (NPCS below) is the GAME's surface:
 * the arena's NPC select, the sprite lab's NPC subjects, the renderer's
 * family/tint lookup. The sim reads an INJECTABLE run catalog
 * (`State.npcs` — default NPCS; `buildNpcCatalog` for scenario fixtures)
 * because `npc.store` is user content that changes freely — a scenario must
 * never depend on what a human happens to have authored in it.
 *
 * Kinds, not entities: an NPC in the sim is a small integer id + a `kind`
 * string that keys into this table. The kind is the PERMANENT wire id —
 * independent of family and shade, never renamed. The client renders an NPC
 * via the committed NPC pipeline
 * frames (`atlas.npcs[family]` keyed by the `family` field, the `tint` is
 * the frame set within it).
 *
 * Adding an NPC is STORE DATA (one block in npc.store, or the knobs app's NPCs
 * editor — zero code). Adding a new SPRITE FAMILY is pipeline work: one
 * `spec/` + builder in tools/npcpipeline (see
 * docs/techniques/procedural-npc-pipeline.md). The sim reads only these
 * stats; it never generates or renders.
 *
 * All combat values are integers (AGENTS §3); speeds are authored in ticks
 * and consumed deterministically by the sim.
 */
import { NPCS_DATA } from "./npcs.data.js";
import { LOOT } from "./loot.js";

/**
 * NPC combat stats, data-driven so a new NPC carries its own numbers and the
 * arena / game can test it without code. The combat model is the ACCURACY
 * CONTEST (sim/combat.ts): the NPC's `attack` points meet the defender's
 * bucket points on a bounded curve to give the HIT CHANCE, then a hit lands
 * the NPC's full damage roll — defense never changes the damage. All values
 * are integers (AGENTS §3); speeds are authored in ticks (the sim consumes
 * them directly; clients display the × TICK_MS ms equivalent, display-only).
 *
 * The editor exposes only the supported combat and movement fields below.
 * `combatLevel` remains a legacy display override for existing fixtures.
 */
/** Supported authoring fields. Blank values retain the sim's defaults. */
export const NPC_STAT_FIELDS = [
  { key: "maxHp", label: "Hitpoints", min: 1 },
  { key: "atkMin", label: "Minimum damage", min: 0 },
  { key: "atkMax", label: "Maximum damage", min: 0 },
  { key: "speedTicks", label: "Attack cadence (ticks)", min: 1 },
  { key: "attack", label: "Attack points", min: 0 },
  { key: "meleeDef", label: "Melee defence", min: 0 },
  { key: "magicDef", label: "Magic defence", min: 0 },
  { key: "range", label: "Attack reach (tiles)", min: 1 },
  { key: "moveSpeedTicks", label: "Walk cadence (ticks)", min: 1 },
] as const;

export interface NpcStats {
  /** Hitpoints. */
  maxHp?: number;
  /** Damage range, inclusive (integers). FULL damage lands on a hit —
   *  never mitigated by defense. */
  atkMin?: number;
  atkMax?: number;
  /** Swing cadence in TICKS (one full swing; × TICK_MS = the ms a client
   *  displays). The sim consumes ticks directly. */
  speedTicks?: number;
  /** ATTACK POINTS (the contest value — vs the hero's armor bucket picked
   *  by this NPC's combat style: melee|ranged → meleeDef, magic → magicDef).
   *  Replaces the old `accuracy` percent (shared/constants.ts). */
  attack?: number;
  /** Defense POINTS vs melee+ranged attacks (contest curve, never soaks
   *  damage — shared/constants.ts). */
  meleeDef?: number;
  /** Defense POINTS vs magic attacks. */
  magicDef?: number;
  /** Melee reach in tiles (Chebyshev distance). 1 = adjacent. */
  range?: number;
  /** WALK cadence in TICKS per tile (movement speed; higher = slower — the
   *  NPC analogue of the global MOVE_COST_TICKS). Absent = the hero's
   *  MOVE_COST_TICKS (the floor's shared walking pace). */
  moveSpeedTicks?: number;
  /** DISPLAY combat level — normally ABSENT, because it is DERIVED from the
   *  stats (`npcCombatLevel`, shared/combat.ts). Present = an explicit
   *  OVERRIDE: the human pinned this entry's number and retuning its stats
   *  will not move it. Never read by the sim; every level surface resolves it
   *  through `npcDisplayLevel` so the override can't be half-honoured. */
  combatLevel?: number;
  /** Open-ended: future stats parse from the store and ride along here. */
  [key: string]: number | undefined;
}

/**
 * The NPC's COMBAT STYLE (npc.store `style` field). It is the delivery of
 * the attack and the client's effect look:
 * - "melee" — a swing at any range (no missile — a long weapon is still a
 *   swing; the `range` stat is the reach, the style is the delivery);
 * - "magic" — a projectile (the client draws the flight + impact;
 *   the sim resolves it identically at impact, same two rolls);
 * - "ranged" — reserved for the future bow (in the schema now so the store
 *   and the editor can carry it; no family offers it yet, so no entry can
 *   use it yet, and the client has no ranged look yet — it would render the
 *   magic orb until one lands).
 *
 * The FAMILY's anims gate what the editor may pick: a family declares its
 * supported styles in the pipeline spec's `combat_styles` (projected to the
 * atlas + the editor's /spec); a magic attack needs a magic anim. The sim
 * only ever sees the entry's chosen style (npcStyle) — it does not read the
 * family metadata (that is authoring-time validation, done at save).
 */
export type CombatStyle = "melee" | "magic" | "ranged";

export interface NpcDef {
  /** Stable kind string — the permanent wire id (keys the sim state,
   *  scenarios, replays, the arena select), independent of sprite appearance. */
  kind: string;
  /** Display name (arena select list, HUD). */
  name: string;
  /** The SPRITE FAMILY — the NPC pipeline creature (atlas.npcs[family]).
   *  The family is the art; the entry is the numbers. */
  family: string;
  /** The NPC pipeline tint to render (atlas.npcs[family][tint]) — "canon"
   *  (unshaded) or the shade hex without '#'. */
  tint: string;
  /** The shade hex (#rrggbb) or null (pipeline canon colours). */
  shade: string | null;
  /**
   * May share a tile with another NPC — only when BOTH are stackable is a
   * shared tile allowed (sim/combat.ts tileBlocked / movement blocking). A
   * non-stackable NPC never shares a tile with anything, and it BLOCKS
   * other NPCs' paths (queue / re-route around). Default false.
   */
  stackable?: boolean;
  /**
   * Can this NPC be force-moved by an ability (dragged into a Black Hole's
   * clump)? Default true — immovable is the opt-in exception (the dragon).
   * An immovable NPC is NOT dragged but is still HIT: the pull misses its
   * body, the well's strike doesn't. Consulted by any ability that
   * force-moves NPCs (sim/combat.ts); the sim never special-cases a kind.
   */
  movable?: boolean;
  /** The entry's combat style (see CombatStyle). Absent = "melee" (the
   *  default — the store parser resolves it; scenario fixtures predate the
   *  field and omit it). */
  style?: CombatStyle;
  /** The loot table (a loot.store id) this creature's death rolls —
   *  design/loot.md: one mechanic, the boss is just an NPC with a better
   *  table. null = no table: the death drops no items (its bones decal
   *  behaves as it does today — the drops_bones flag lives on the TABLE).
   *  Consumed by the sim's death roll (loot.md build 3). */
  lootTable: number | null;
  /**
   * The BOLT LOOK — the hue this creature's projectile flies, and its head
   * radius in px at 1x (npc.store `spellColor` / `spellRadius`). PRESENTATION
   * ONLY: the sim never reads either (delivery is `style` + `range`); the
   * client's effect layer looks them up off the catalog by the enemy's kind
   * (effects.ts), exactly as a magic weapon's spell triple drives the hero's
   * bolt. Absent = the canon bolt (the shade's committed look), which is what
   * every entry predating the field omits. Legal only on a non-melee style —
   * a melee creature never fires a projectile to recolour.
   */
  spellColor?: string;
  spellRadius?: number;
  /** Combat stats (data-driven — see NpcStats). */
  stats: NpcStats;
}

/**
 * The bolt LOOK, validated. Shared by the generated-data and the fixture
 * validator so a scenario can pin the same shape the store emits. Absent has
 * THREE legal encodings (omitted, explicit null, or the store never writing
 * the line) and all must survive — the lootTable trap, one field over.
 * Rejected on a melee entry: it never fires a projectile to recolour.
 */
function readSpell(
  where: string,
  kind: string,
  style: CombatStyle | undefined,
  spellColor: unknown,
  spellRadius: unknown,
): { spellColor?: string; spellRadius?: number } {
  const spell: { spellColor?: string; spellRadius?: number } = {};
  if (spellColor !== undefined && spellColor !== null) {
    if (typeof spellColor !== "string" || !/^#[0-9a-f]{6}$/.test(spellColor)) {
      throw new Error(`${where}: ${kind}: spellColor must be a #rrggbb hex (got ${String(spellColor)})`);
    }
    spell.spellColor = spellColor;
  }
  if (spellRadius !== undefined && spellRadius !== null) {
    if (!Number.isInteger(spellRadius) || (spellRadius as number) < 1) {
      throw new Error(`${where}: ${kind}: spellRadius must be an integer >= 1 (got ${String(spellRadius)})`);
    }
    spell.spellRadius = spellRadius as number;
  }
  if ((spell.spellColor !== undefined || spell.spellRadius !== undefined)
      && (style ?? "melee") === "melee") {
    throw new Error(`${where}: ${kind}: spellColor/spellRadius need a non-melee style (a melee npc never fires a projectile)`);
  }
  return spell;
}

/**
 * All NPC kinds. Built from the generated data with the same validation
 * discipline the hand-written catalog had: unique kinds, integer stats,
 * valid sprite tint and shade. A bad store is a
 * build error, never a runtime surprise.
 */
function buildCatalog(): readonly NpcDef[] {
  const kinds = new Set<string>();
  const out: NpcDef[] = [];
  // Widen the `as const` literal types: the generated data is validated here
  // at runtime (a hand-edited npcs.data.ts must fail loudly, not
  // type-trick its way past the checks).
  const data: readonly unknown[] = NPCS_DATA;
  for (const r of data) {
    const raw = r as {
      kind?: unknown; name?: unknown; family?: unknown; tint?: unknown;
      shade?: unknown; style?: unknown; stackable?: unknown; movable?: unknown;
      lootTable?: unknown; spellColor?: unknown; spellRadius?: unknown; stats?: unknown;
    };
    if (typeof raw.kind !== "string" || raw.kind === "") {
      throw new Error(`npcs.data: bad kind ${String(raw.kind)} (${String(raw.name)})`);
    }
    const kind = raw.kind;
    if (raw.style !== undefined && raw.style !== "melee" && raw.style !== "magic" && raw.style !== "ranged") {
      throw new Error(`npcs.data: ${kind}: bad style ${String(raw.style)} (want melee | magic | ranged)`);
    }
    if (kinds.has(kind)) {
      throw new Error(`npcs.data: duplicate kind ${kind} (${String(raw.name)})`);
    }
    kinds.add(kind);
    if (typeof raw.name !== "string" || raw.name === "") {
      throw new Error(`npcs.data: kind ${kind}: missing name`);
    }
    if (typeof raw.family !== "string" || raw.family === "") {
      throw new Error(`npcs.data: ${kind}: missing family`);
    }
    if (typeof raw.tint !== "string" || raw.tint === "") {
      throw new Error(`npcs.data: ${kind}: missing tint`);
    }
    const shade = typeof raw.shade === "string" ? raw.shade : null;
    if (shade === null && raw.tint !== "canon") {
      throw new Error(`npcs.data: ${kind}: unshaded entries render the "canon" tint (got ${raw.tint})`);
    }
    if (shade !== null && !/^#[0-9a-f]{6}$/.test(shade)) {
      throw new Error(`npcs.data: ${kind}: bad shade ${shade} (want #rrggbb)`);
    }
    if (shade !== null && `#${raw.tint}` !== shade.toLowerCase()) {
      throw new Error(`npcs.data: ${kind}: tint ${raw.tint} does not match shade ${shade}`);
    }
    if (raw.movable !== undefined && typeof raw.movable !== "boolean") {
      throw new Error(`npcs.data: ${kind}: movable must be a boolean`);
    }
    // The loot-table reference: THREE encodings of "no table" are all legal
    // and must all survive (the roomId pattern): the store omits the line,
    // the data module writes an EXPLICIT null (the emitter's choice), and a
    // hand-written module may omit the field (fixtures predate the field).
    // A dangling id is a build error — the loot.store cross-check, run twice
    // (at store save and HERE).
    let lootTable: number | null = null;
    if (raw.lootTable !== undefined && raw.lootTable !== null) {
      if (!Number.isInteger(raw.lootTable) || (raw.lootTable as number) < 0) {
        throw new Error(`npcs.data: ${kind}: lootTable must be a non-negative integer or null (got ${String(raw.lootTable)})`);
      }
      lootTable = raw.lootTable as number;
      if (!LOOT.some((t) => t.id === lootTable)) {
        throw new Error(`npcs.data: ${kind}: loot table ${lootTable} is not in the loot catalog (loot.store) — fix the reference or the store`);
      }
    }
    const stats: NpcStats = {};
    const rawStats = raw.stats as Record<string, unknown> | undefined;
    for (const [k, v] of Object.entries(rawStats ?? {})) {
      if (typeof v !== "number" || !Number.isInteger(v) || v < 0) {
        throw new Error(`npcs.data: ${kind}: stat ${k} must be a non-negative integer`);
      }
      if ((k === "accuracy" || k === "defense") && v > 100) {
        throw new Error(`npcs.data: ${kind}: stat ${k} is a percent (0-100)`);
      }
      stats[k] = v;
    }
    out.push({
      kind,
      name: raw.name,
      family: raw.family,
      tint: raw.tint,
      shade,
      style: raw.style as CombatStyle | undefined,
      stackable: raw.stackable === true,
      movable: raw.movable === false ? false : undefined,
      lootTable,
      ...readSpell("npcs.data", kind, raw.style as CombatStyle | undefined,
                   raw.spellColor, raw.spellRadius),
      stats,
    });
  }
  return out;
}

/** All NPC kinds, by kind string (may have gaps — permanent wire ids). */
export const NPCS: readonly NpcDef[] = buildCatalog();

/** A stable kind string (the permanent wire id — scenarios/replays carry it). */
export type NpcKind = string;

/** Catalog lookup; undefined for unknown kinds (the sim/arena reject those). */
export function npcByKind(kind: string): NpcDef | undefined {
  return NPCS.find((n) => n.kind === kind);
}

/** The entry's combat style, defaulting to "melee" (absent in fixtures that
 *  predate the field; the store parser always resolves it). */
export function npcStyle(def: NpcDef | undefined): CombatStyle {
  return def?.style ?? "melee";
}

/** Lookup in a RUN's NPC catalog (State.npcs — scenarios/tests carry their
 *  own; the committed NPCS is the default). Undefined for unknown kinds. */
export function findNpc(npcs: readonly NpcDef[], kind: string): NpcDef | undefined {
  return npcs.find((n) => n.kind === kind);
}

/**
 * Build a RUN's NPC catalog from fixture records (a scenario's
 * `config.npcs` — the store entry shape: kind/name/family/tint, optional
 * shade + stackable, integer stats). The scenario's fixture catalog is
 * COMPLETE for that run — it does not merge with the committed store, so a
 * scenario is a self-contained experiment that never depends on (or breaks
 * because of) what a human happens to have authored in `npc.store`. Same
 * validation discipline as the generated projection (buildCatalog above):
 * bad data is a build error, never a runtime surprise. `shade` ("#rrggbb")
 * may be present or null; it must agree with the tint.
 */
export function buildNpcCatalog(records: readonly unknown[]): readonly NpcDef[] {
  const kinds = new Set<string>();
  const out: NpcDef[] = [];
  for (const r of records) {
    const raw = r as {
      kind?: unknown; name?: unknown; family?: unknown; tint?: unknown;
      shade?: unknown; style?: unknown; stackable?: unknown; movable?: unknown;
      lootTable?: unknown; spellColor?: unknown; spellRadius?: unknown; stats?: unknown;
    } | null;
    if (!raw || typeof raw !== "object") {
      throw new Error(`npcs fixture: entry is not an object (${String(r)})`);
    }
    if (raw.style !== undefined && raw.style !== "melee" && raw.style !== "magic" && raw.style !== "ranged") {
      throw new Error(`npcs fixture: bad style ${String(raw.style)} (want melee | magic | ranged)`);
    }
    if (typeof raw.kind !== "string" || raw.kind === "") {
      throw new Error(`npcs fixture: bad kind ${String(raw.kind)}`);
    }
    const kind = raw.kind;
    if (kinds.has(kind)) throw new Error(`npcs fixture: duplicate kind ${kind}`);
    kinds.add(kind);
    if (typeof raw.name !== "string" || raw.name === "") {
      throw new Error(`npcs fixture ${kind}: missing name`);
    }
    if (typeof raw.family !== "string" || raw.family === "") {
      throw new Error(`npcs fixture ${kind}: missing family`);
    }
    if (typeof raw.tint !== "string" || raw.tint === "") {
      throw new Error(`npcs fixture ${kind}: missing tint`);
    }
    const shade = raw.shade === null || raw.shade === undefined ? null : raw.shade;
    if (shade !== null && (typeof shade !== "string" || !/^#[0-9a-fA-F]{6}$/.test(shade))) {
      throw new Error(`npcs fixture ${kind}: bad shade ${String(shade)} (want #rrggbb or null)`);
    }
    if (shade === null && raw.tint !== "canon") {
      throw new Error(`npcs fixture ${kind}: unshaded entries render the "canon" tint (got ${raw.tint})`);
    }
    if (shade !== null && `#${raw.tint}` !== shade.toLowerCase()) {
      throw new Error(`npcs fixture ${kind}: tint ${raw.tint} does not match shade ${shade}`);
    }
    if (raw.stackable !== undefined && raw.stackable !== true && raw.stackable !== false) {
      throw new Error(`npcs fixture ${kind}: stackable must be true/false`);
    }
    if (raw.movable !== undefined && raw.movable !== true && raw.movable !== false) {
      throw new Error(`npcs fixture ${kind}: movable must be true/false`);
    }
    // Shape-validated only (no LOOT cross-check): a scenario fixture catalog
    // is COMPLETE for that run and never depends on the committed loot.store.
    let lootTable: number | null = null;
    if (raw.lootTable !== undefined && raw.lootTable !== null) {
      if (!Number.isInteger(raw.lootTable) || (raw.lootTable as number) < 0) {
        throw new Error(`npcs fixture ${kind}: lootTable must be a non-negative integer or null`);
      }
      lootTable = raw.lootTable as number;
    }
    const stats: NpcStats = {};
    const rawStats = raw.stats as Record<string, unknown> | undefined;
    for (const [k, v] of Object.entries(rawStats ?? {})) {
      if (typeof v !== "number" || !Number.isInteger(v) || v < 0) {
        throw new Error(`npcs fixture ${kind}: stat ${k} must be a non-negative integer`);
      }
      if ((k === "accuracy" || k === "defense") && v > 100) {
        throw new Error(`npcs fixture ${kind}: stat ${k} is a percent (0-100)`);
      }
      stats[k] = v;
    }
    out.push({
      kind,
      name: raw.name,
      family: raw.family,
      tint: raw.tint,
      shade,
      style: raw.style as CombatStyle | undefined,
      stackable: raw.stackable === true,
      movable: raw.movable === false ? false : undefined,
      lootTable,
      ...readSpell("npcs fixture", kind, raw.style as CombatStyle | undefined,
                   raw.spellColor, raw.spellRadius),
      stats,
    });
  }
  return out;
}
